// START IMPORTS
import { Box, Collapse } from '@ltvco/refresh-lib/theme';
import { useState } from 'react';

import {
  AccordionCard,
  AccordionCardButton,
  Chevron,
  ExpandPanelInner,
  ExpandRowCount,
  ExpandRowLabel,
  ExpandRowMain,
  ExpandRowPreview,
  FieldList,
  MetricCard,
  MetricsGrid,
  ReportFieldRow,
  SectionDivider,
  StatusFieldIcon,
  SubsectionTitle,
  VStack,
} from '../../components';
import {
  formatCurrency,
  formatMileage,
  formatMonthYear,
  hasValue,
} from '../utils';

import type {
  VehicleAccidentRecord,
  VehicleInvestigation,
  VehicleLienRecord,
  VehicleOwnershipRecord,
  VehicleReportData,
  VehicleSafetyRating,
  VehicleWarranty,
} from 'components/DeepSearch/types';
// END IMPORTS

// START DERIVED_DATA_AND_UTILS
function moneyFromString(value: string): string {
  if (!value) {
    return '';
  }
  return formatCurrency(value);
}

function moneyFromNumber(value: number | null | undefined): string {
  if (value == null) {
    return '';
  }
  return `$${value.toLocaleString('en-US')}`;
}

// Shared inner row layout for accordion card headers
const CardHeaderInner = ({
  label,
  preview,
  count,
  expandable,
  children,
}: {
  label: string;
  preview?: string;
  count?: number;
  expandable?: boolean;
  children?: React.ReactNode;
}) => (
  <>
    <ExpandRowMain>
      <ExpandRowLabel>{label}</ExpandRowLabel>
      {preview && <ExpandRowPreview>{preview}</ExpandRowPreview>}
    </ExpandRowMain>

    {count != null && <ExpandRowCount count={count} />}

    {children}

    {!expandable && <Chevron open={false} hidden flexShrink={0} />}
  </>
);
// END DERIVED_DATA_AND_UTILS

// START VALUATION_SECTION
export const VehicleValueSection = ({ data }: { data: VehicleReportData }) => {
  const marketValue = moneyFromString(data.marketValue);
  const yearlyCost = moneyFromString(data.estimatedYearlyCost);
  const msrp = moneyFromNumber(data.marketValueDetails?.msrp);
  const priceHigh = data.marketValueDetails?.priceRangeHighest;
  const priceLow = data.marketValueDetails?.priceRangeLowest;

  const hasAny = Boolean(marketValue || yearlyCost || msrp);
  if (!hasAny) {
    return null;
  }

  const priceRangeLine =
    priceHigh != null && priceLow != null && priceHigh !== priceLow
      ? `Price range: ${moneyFromNumber(priceLow)} – ${moneyFromNumber(
          priceHigh
        )}`
      : '';

  return (
    <>
      <SectionDivider />
      <SubsectionTitle>Mileage &amp; value</SubsectionTitle>
      <MetricsGrid>
        {marketValue && (
          <MetricCard
            label="Market value"
            value={marketValue}
            sub={priceRangeLine || undefined}
          />
        )}
        {msrp && <MetricCard label="Original MSRP" value={msrp} />}
        {yearlyCost && (
          <MetricCard label="Est. yearly cost" value={yearlyCost} />
        )}
        {hasValue(data.estimatedMileage) && (
          <MetricCard
            label="Estimated mileage"
            value={formatMileage(data.estimatedMileage)}
            sub={
              data.mileageAsOfDate ? `as of ${data.mileageAsOfDate}` : undefined
            }
          />
        )}
        {(data.subtype === 'vin' || data.subtype === 'plate') &&
          hasValue(data.lastReportedMileage) && (
            <MetricCard
              label="Last reported mileage"
              value={formatMileage(data.lastReportedMileage)}
              sub={data.lastReportedMileageDate || undefined}
            />
          )}
      </MetricsGrid>
    </>
  );
};
// END VALUATION_SECTION

// START VEHICLE_DETAILS_SECTION
export const VehicleDetailsSection = ({
  data,
}: {
  data: VehicleReportData;
}) => {
  const specs = data.specs;
  const hasVin =
    (data.subtype === 'vin' || data.subtype === 'plate') && hasValue(data.vin);

  const fieldRows: Array<{ label: string; value: string }> = [];

  if (hasVin) {
    fieldRows.push({ label: 'VIN', value: data.vin });
  }
  if (hasValue(data.bodyStyle)) {
    fieldRows.push({ label: 'Body style', value: data.bodyStyle });
  }
  if (specs) {
    if (specs.fuelType || specs.engineDisplacementLiters != null) {
      const engineLabel = [
        specs.engineDisplacementLiters != null
          ? `${specs.engineDisplacementLiters}L`
          : '',
        specs.fuelType,
      ]
        .filter(Boolean)
        .join(' · ');
      if (engineLabel) {
        fieldRows.push({ label: 'Engine', value: engineLabel });
      }
    }
    if (specs.transmissionType) {
      fieldRows.push({ label: 'Transmission', value: specs.transmissionType });
    }
    if (specs.drivenWheels) {
      fieldRows.push({ label: 'Drivetrain', value: specs.drivenWheels });
    }
    if (specs.cityMpg != null || specs.highwayMpg != null) {
      const mpgLabel =
        specs.cityMpg != null && specs.highwayMpg != null
          ? `${specs.cityMpg} city / ${specs.highwayMpg} hwy`
          : specs.cityMpg != null
          ? `${specs.cityMpg} city`
          : `${specs.highwayMpg} hwy`;
      fieldRows.push({ label: 'Fuel economy', value: mpgLabel });
    }
  }

  if (fieldRows.length === 0) {
    return null;
  }

  return (
    <>
      <SectionDivider />
      <SubsectionTitle>Vehicle details</SubsectionTitle>
      <FieldList>
        {fieldRows.map(({ label, value }) => (
          <ReportFieldRow key={label} label={label} value={value} />
        ))}
      </FieldList>
    </>
  );
};
// END VEHICLE_DETAILS_SECTION

// START VEHICLE_RECORDS_SECTION
// Extracts "IL" from "Deerfield, IL 60015" or "Lake Orion, MI"
function extractState(cityState: string): string {
  const match = cityState.match(/,\s+([A-Z]{2})\b/);
  return match ? match[1] : '';
}

// Strips zip code: "Deerfield, IL 60015" → "Deerfield, IL"
function formatCityState(cityState: string): string {
  return cityState.replace(/\s+\d{5}(-\d{4})?$/, '').trim();
}

// Static (non-expandable) accordion card row
const StaticAccordionRow = ({
  label,
  preview,
  count,
}: {
  label: string;
  preview?: string;
  count: number;
}) => (
  <AccordionCard>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '12px 16px',
        minHeight: 44,
      }}
    >
      <CardHeaderInner label={label} preview={preview} count={count} />
    </Box>
  </AccordionCard>
);

// Expandable accordion card row (Recalls)
const RecallAccordionRow = ({
  recalls,
}: {
  recalls: NonNullable<VehicleReportData['recallDetails']>;
}) => {
  const [open, setOpen] = useState(false);
  const preview = recalls[0]?.components
    ? recalls[0].components.split(':').slice(0, 2).join(' · ')
    : '';

  return (
    <AccordionCard>
      <AccordionCardButton
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <CardHeaderInner
          label="Recalls"
          preview={preview}
          count={recalls.length}
          expandable
        >
          <Chevron
            open={open}
            flexShrink={0}
            transition="transform 0.15s cubic-bezier(0.4,0,0.2,1)"
          />
        </CardHeaderInner>
      </AccordionCardButton>
      <Collapse in={open}>
        <ExpandPanelInner sx={{ px: 2 }}>
          <VStack gap={8}>
            {recalls.map((recall, i) => (
              <Box key={i}>
                {i > 0 && (
                  <Box
                    sx={{
                      borderTop: '1px solid',
                      borderColor: 'divider',
                      mb: 1,
                    }}
                  />
                )}
                <Box
                  component="span"
                  sx={{
                    display: 'block',
                    fontSize: 16,
                    fontWeight: 700,
                    color: 'text.primary',
                    mb: 0.5,
                  }}
                >
                  {recall.components || 'Recall'}
                </Box>
                {recall.summary && (
                  <Box
                    component="span"
                    sx={{
                      display: 'block',
                      fontSize: 14,
                      color: 'text.secondary',
                      lineHeight: 1.4,
                    }}
                  >
                    {recall.summary.length > 200
                      ? `${recall.summary.slice(0, 200)}…`
                      : recall.summary}
                  </Box>
                )}
              </Box>
            ))}
          </VStack>
        </ExpandPanelInner>
      </Collapse>
    </AccordionCard>
  );
};

// Expandable accordion card row (Warranties)
const WarrantyAccordionRow = ({
  warranties,
}: {
  warranties: VehicleWarranty[];
}) => {
  const [open, setOpen] = useState(false);
  const activeCount = warranties.filter((w) => w.isActive).length;
  const firstType = warranties[0]?.type ?? '';
  const preview =
    activeCount > 0 ? `${activeCount} active · ${firstType}` : firstType;

  return (
    <AccordionCard>
      <AccordionCardButton
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <CardHeaderInner
          label="Warranty records"
          preview={preview}
          count={warranties.length}
          expandable
        >
          <Chevron
            open={open}
            flexShrink={0}
            transition="transform 0.15s cubic-bezier(0.4,0,0.2,1)"
          />
        </CardHeaderInner>
      </AccordionCardButton>
      <Collapse in={open}>
        <ExpandPanelInner sx={{ px: 2 }}>
          <VStack gap={0}>
            {warranties.map((warranty, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  py: 1.25,
                  borderBottom:
                    i < warranties.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                }}
              >
                <Box>
                  <Box
                    component="span"
                    sx={{
                      display: 'block',
                      fontSize: 16,
                      fontWeight: 700,
                      color: 'text.primary',
                    }}
                  >
                    {warranty.type}
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      display: 'block',
                      fontSize: 14,
                      color: 'text.secondary',
                    }}
                  >
                    {[warranty.months, warranty.miles]
                      .filter(Boolean)
                      .join(' · ')}
                  </Box>
                </Box>
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    flexShrink: 0,
                    fontSize: 12,
                    fontWeight: 700,
                    color: warranty.isActive ? 'success.dark' : 'error.dark',
                  }}
                >
                  <StatusFieldIcon
                    severity={warranty.isActive ? 'success' : 'error'}
                  />
                  {warranty.isActive ? 'Active' : 'Expired'}
                </Box>
              </Box>
            ))}
          </VStack>
        </ExpandPanelInner>
      </Collapse>
    </AccordionCard>
  );
};

// Expandable accordion card row (Safety)
const SafetyAccordionRow = ({
  ratings,
}: {
  ratings: VehicleSafetyRating[];
}) => {
  const [open, setOpen] = useState(false);
  const first = ratings[0];
  const preview = first
    ? `Rollover ${first.rolloverRating}/5 · overall ${first.overallRating}/5`
    : '';

  const kvRows: Array<[string, string]> = first
    ? [
        ['Overall rating', `${first.overallRating}/5`],
        ['Front crash', `${first.frontCrashRating}/5`],
        ['Side crash', `${first.sideCrashRating}/5`],
        ['Rollover', `${first.rolloverRating}/5`],
      ]
    : [];

  return (
    <AccordionCard>
      <AccordionCardButton
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <CardHeaderInner label="Safety" preview={preview} expandable>
          <Chevron
            open={open}
            flexShrink={0}
            transition="transform 0.15s cubic-bezier(0.4,0,0.2,1)"
          />
        </CardHeaderInner>
      </AccordionCardButton>
      <Collapse in={open}>
        <ExpandPanelInner sx={{ px: 2 }}>
          <Box
            component="span"
            sx={{
              display: 'block',
              fontSize: 12,
              color: 'text.secondary',
              mb: 1,
            }}
          >
            NHTSA ratings
            {first?.vehicleDescription ? ` · ${first.vehicleDescription}` : ''}
          </Box>
          <VStack gap={0}>
            {kvRows.map(([k, v], i) => (
              <Box
                key={k}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 2,
                  alignItems: 'baseline',
                  py: 1,
                  fontSize: 14,
                  borderBottom: i < kvRows.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                }}
              >
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  {k}
                </Box>
                <Box
                  component="span"
                  sx={{ fontWeight: 700, color: 'text.primary' }}
                >
                  {v}
                </Box>
              </Box>
            ))}
          </VStack>
        </ExpandPanelInner>
      </Collapse>
    </AccordionCard>
  );
};

const OwnershipAccordionRow = ({
  records,
}: {
  records: VehicleOwnershipRecord[];
}) => {
  const [open, setOpen] = useState(false);
  const count = records.length;
  const states = records.map((r) => extractState(r.cityState)).filter(Boolean);
  const stateChain = states
    .filter((s, i) => i === 0 || s !== states[i - 1])
    .join(' → ');
  const preview = stateChain
    ? `${count} owner${count !== 1 ? 's' : ''} · ${stateChain}`
    : `${count} owner${count !== 1 ? 's' : ''}`;

  return (
    <AccordionCard>
      <AccordionCardButton
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <CardHeaderInner
          label="Ownership history"
          preview={preview}
          count={count}
          expandable
        >
          <Chevron
            open={open}
            flexShrink={0}
            transition="transform 0.15s cubic-bezier(0.4,0,0.2,1)"
          />
        </CardHeaderInner>
      </AccordionCardButton>
      <Collapse in={open}>
        <ExpandPanelInner sx={{ px: 2 }}>
          <VStack gap={0}>
            {records.map((record, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 2,
                  py: 1.5,
                  borderBottom: i < records.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Box
                    component="span"
                    sx={{
                      display: 'block',
                      fontSize: 16,
                      fontWeight: 700,
                      color: 'text.primary',
                      mb: 0.25,
                    }}
                  >
                    {record.isNew ? 'New' : 'Used'}
                    {record.date ? ` · ${formatMonthYear(record.date)}` : ''}
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      display: 'block',
                      fontSize: 14,
                      color: 'text.secondary',
                    }}
                  >
                    {[
                      record.seller || 'Unknown seller',
                      record.odometer != null
                        ? `${record.odometer.toLocaleString('en-US')} mi`
                        : '',
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </Box>
                  {record.lienHolder && (
                    <Box
                      component="span"
                      sx={{
                        display: 'block',
                        fontSize: 12,
                        color: 'text.disabled',
                        mt: 0.25,
                      }}
                    >
                      Lienholder: {record.lienHolder}
                    </Box>
                  )}
                </Box>
                {record.cityState && (
                  <Box
                    component="span"
                    sx={{
                      fontSize: 14,
                      color: 'text.secondary',
                      flexShrink: 0,
                      textAlign: 'right',
                    }}
                  >
                    {formatCityState(record.cityState)}
                  </Box>
                )}
              </Box>
            ))}
          </VStack>
        </ExpandPanelInner>
      </Collapse>
    </AccordionCard>
  );
};

const AccidentAccordionRow = ({
  accidents,
}: {
  accidents: VehicleAccidentRecord[];
}) => {
  const [open, setOpen] = useState(false);
  const first = accidents[0];
  const preview = first
    ? [first.date ? formatMonthYear(first.date) : '', first.location]
        .filter(Boolean)
        .join(' · ')
    : '';

  return (
    <AccordionCard>
      <AccordionCardButton
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <CardHeaderInner
          label="Accidents"
          preview={preview}
          count={accidents.length}
          expandable
        >
          <Chevron
            open={open}
            flexShrink={0}
            transition="transform 0.15s cubic-bezier(0.4,0,0.2,1)"
          />
        </CardHeaderInner>
      </AccordionCardButton>
      <Collapse in={open}>
        <ExpandPanelInner sx={{ px: 2 }}>
          <VStack gap={0}>
            {accidents.map((accident, i) => (
              <Box
                key={i}
                sx={{
                  py: 1.25,
                  borderBottom: i < accidents.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: 'block',
                    fontSize: 16,
                    fontWeight: 700,
                    color: 'text.primary',
                  }}
                >
                  {accident.date ? formatMonthYear(accident.date) : 'Accident'}
                </Box>
                {accident.location && (
                  <Box
                    component="span"
                    sx={{
                      display: 'block',
                      fontSize: 14,
                      color: 'text.secondary',
                    }}
                  >
                    {accident.location}
                  </Box>
                )}
                {accident.mannerOfCollision && (
                  <Box
                    component="span"
                    sx={{
                      display: 'block',
                      fontSize: 14,
                      color: 'text.secondary',
                    }}
                  >
                    {accident.mannerOfCollision}
                  </Box>
                )}
                {accident.crashSeverity && (
                  <Box
                    component="span"
                    sx={{
                      display: 'block',
                      fontSize: 12,
                      color: 'text.disabled',
                    }}
                  >
                    {accident.crashSeverity}
                  </Box>
                )}
              </Box>
            ))}
          </VStack>
        </ExpandPanelInner>
      </Collapse>
    </AccordionCard>
  );
};

const LienAccordionRow = ({ liens }: { liens: VehicleLienRecord[] }) => {
  const [open, setOpen] = useState(false);
  const preview = liens[0]?.lienholder ?? '';

  return (
    <AccordionCard>
      <AccordionCardButton
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <CardHeaderInner
          label="Liens"
          preview={preview}
          count={liens.length}
          expandable
        >
          <Chevron
            open={open}
            flexShrink={0}
            transition="transform 0.15s cubic-bezier(0.4,0,0.2,1)"
          />
        </CardHeaderInner>
      </AccordionCardButton>
      <Collapse in={open}>
        <ExpandPanelInner sx={{ px: 2 }}>
          <VStack gap={0}>
            {liens.map((lien, i) => (
              <Box
                key={i}
                sx={{
                  py: 1.25,
                  borderBottom: i < liens.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: 'block',
                    fontSize: 16,
                    fontWeight: 700,
                    color: 'text.primary',
                  }}
                >
                  {lien.lienholder}
                </Box>
                {lien.date && (
                  <Box
                    component="span"
                    sx={{
                      display: 'block',
                      fontSize: 14,
                      color: 'text.secondary',
                    }}
                  >
                    {formatMonthYear(lien.date)}
                  </Box>
                )}
              </Box>
            ))}
          </VStack>
        </ExpandPanelInner>
      </Collapse>
    </AccordionCard>
  );
};

const InvestigationAccordionRow = ({
  investigations,
}: {
  investigations: VehicleInvestigation[];
}) => {
  const [open, setOpen] = useState(false);
  const first = investigations[0];
  const preview = first?.subject ?? '';

  return (
    <AccordionCard>
      <AccordionCardButton
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <CardHeaderInner
          label="Open investigations"
          preview={preview}
          count={investigations.length}
          expandable
        >
          <Chevron
            open={open}
            flexShrink={0}
            transition="transform 0.15s cubic-bezier(0.4,0,0.2,1)"
          />
        </CardHeaderInner>
      </AccordionCardButton>
      <Collapse in={open}>
        <ExpandPanelInner sx={{ px: 2 }}>
          <VStack gap={0}>
            {investigations.map((inv, i) => (
              <Box
                key={i}
                sx={{
                  py: 1.25,
                  borderBottom:
                    i < investigations.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: 'block',
                    fontSize: 16,
                    fontWeight: 700,
                    color: 'text.primary',
                    mb: 0.25,
                  }}
                >
                  {inv.subject || inv.nhtsaActionNumber}
                </Box>
                {inv.nhtsaActionNumber && inv.subject && (
                  <Box
                    component="span"
                    sx={{
                      display: 'block',
                      fontSize: 14,
                      color: 'text.secondary',
                    }}
                  >
                    {inv.nhtsaActionNumber}
                  </Box>
                )}
                {inv.components.length > 0 && (
                  <Box
                    component="span"
                    sx={{
                      display: 'block',
                      fontSize: 12,
                      color: 'text.disabled',
                      mt: 0.25,
                    }}
                  >
                    {inv.components.join(', ')}
                  </Box>
                )}
              </Box>
            ))}
          </VStack>
        </ExpandPanelInner>
      </Collapse>
    </AccordionCard>
  );
};

export const VehicleRecordsSection = ({
  data,
}: {
  data: VehicleReportData;
}) => {
  const hasOwnershipDetails = (data.ownershipHistoryDetails?.length ?? 0) > 0;
  const hasAccidentDetails = (data.accidentDetails?.length ?? 0) > 0;
  const hasLienDetails = (data.lienDetails?.length ?? 0) > 0;
  const hasInvestigationDetails = (data.investigationDetails?.length ?? 0) > 0;
  const hasRecallDetails = (data.recallDetails?.length ?? 0) > 0;
  const hasWarrantyDetails = (data.warrantyDetails?.length ?? 0) > 0;
  const hasSafetyRatings = (data.safetyRatings?.length ?? 0) > 0;

  return (
    <>
      <SectionDivider />
      <SubsectionTitle>Vehicle records</SubsectionTitle>
      <VStack gap={10}>
        {hasOwnershipDetails ? (
          <OwnershipAccordionRow records={data.ownershipHistoryDetails!} />
        ) : (
          <StaticAccordionRow
            label="Ownership history"
            count={data.ownershipHistory}
          />
        )}
        {hasAccidentDetails ? (
          <AccidentAccordionRow accidents={data.accidentDetails!} />
        ) : (
          <StaticAccordionRow label="Accidents" count={data.accidents} />
        )}
        <StaticAccordionRow label="Damage history" count={data.damageHistory} />
        {hasLienDetails ? (
          <LienAccordionRow liens={data.lienDetails!} />
        ) : (
          <StaticAccordionRow label="Liens" count={data.liens} />
        )}
        {hasInvestigationDetails ? (
          <InvestigationAccordionRow
            investigations={data.investigationDetails!}
          />
        ) : data.openInvestigations != null ? (
          <StaticAccordionRow
            label="Open investigations"
            count={data.openInvestigations}
          />
        ) : null}
        {data.subtype === 'ymm' && (
          <StaticAccordionRow
            label="Ownership cost records"
            count={data.ownershipCostRecords}
          />
        )}
        {(data.subtype === 'vin' || data.subtype === 'plate') && (
          <StaticAccordionRow
            label="Sales listings"
            count={data.salesListings}
          />
        )}
        {hasRecallDetails ? (
          <RecallAccordionRow recalls={data.recallDetails!} />
        ) : (
          <StaticAccordionRow label="Recalls" count={data.recalls} />
        )}
        {hasWarrantyDetails ? (
          <WarrantyAccordionRow warranties={data.warrantyDetails!} />
        ) : (
          <StaticAccordionRow
            label="Warranty records"
            count={data.warrantyRecords}
          />
        )}
        <StaticAccordionRow
          label="Active warranties"
          count={data.activeWarranties}
        />
        {hasSafetyRatings && (
          <SafetyAccordionRow ratings={data.safetyRatings!} />
        )}
      </VStack>
    </>
  );
};
// END VEHICLE_RECORDS_SECTION
